/**
 * FlahaSOIL v2 — Soil-test wizard page.
 *
 * Owns the draft state and step navigation. The visible step list is
 * derived from `draft.testLevel`. Submit chains create-sample →
 * create-test → calculate against the active API client.
 *
 * The wizard always runs inside a `Project`. A `?projectId`
 * search parameter preselects the project; otherwise the user picks
 * one in the sample-info step. The "Next" button on that step is
 * disabled until a project is selected so we never call
 * `createSoilSample` without an owning project.
 */
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SoilTestLevel } from "@flaha/shared-types";

import { AdvancedInputStep } from "../features/soil-test/components/AdvancedInputStep";
import { ModerateInputStep } from "../features/soil-test/components/ModerateInputStep";
import { PreliminaryInputStep } from "../features/soil-test/components/PreliminaryInputStep";
import { ReviewStep } from "../features/soil-test/components/ReviewStep";
import { SampleInfoStep } from "../features/soil-test/components/SampleInfoStep";
import { SoilTestStepper } from "../features/soil-test/components/SoilTestStepper";
import {
	TestLevelStep,
	type TestLevelPlanAccess,
} from "../features/soil-test/components/TestLevelStep";
import {
	EMPTY_DRAFT,
	type SoilTestDraft,
	toCreateSoilTestRequest,
} from "../features/soil-test/state/soilTestDraft";
import { visibleStepsForLevel } from "../features/soil-test/utils/soilTestDefaults";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { getApiClient } from "../services/apiClientProvider";
import { useAuth } from "../auth";

type SubmissionStage = "idle" | "sample-created" | "test-created";

interface SubmissionCheckpoint {
	sampleId?: string;
	soilTestId?: string;
}

export function SoilTestWizardPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const preselectedProjectId = searchParams.get("projectId");
	const { activeOrganization } = useAuth();
	const theme = useTheme();
	const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
	const prefersReducedMotion = useMediaQuery(
		"(prefers-reduced-motion: reduce)",
		{ noSsr: true }
	);

	const [draft, setDraft] = useState<SoilTestDraft>(() => ({
		...EMPTY_DRAFT,
		sampleInfo: {
			...EMPTY_DRAFT.sampleInfo,
			projectId: preselectedProjectId ?? null,
		},
	}));
	const [activeStep, setActiveStep] = useState(0);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [submissionStage, setSubmissionStage] = useState<SubmissionStage>("idle");
	const [planAccess, setPlanAccess] = useState<TestLevelPlanAccess | null>(null);
	const submissionCheckpoint = useRef<SubmissionCheckpoint>({});
	const stepContentRef = useRef<HTMLDivElement | null>(null);
	const errorRef = useRef<HTMLDivElement | null>(null);
	const previousStep = useRef(0);
	const previousProjectParam = useRef(preselectedProjectId);
	const previousOrganizationId = useRef(activeOrganization?.id);

	usePageHeader({
		title: "New soil test",
		subtitle: preselectedProjectId
			? "Guided wizard · project preselected"
			: "Guided wizard · capture sample, analyse, interpret",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			...(preselectedProjectId
				? [
						{ label: "Projects", to: "/projects" },
						{ label: "Project", to: `/projects/${preselectedProjectId}` },
					]
				: [{ label: "Projects", to: "/projects" }]),
			{ label: "New soil test" },
		],
	});

	// A changed route query is authoritative. Organization changes are handled
	// separately so an old query never re-selects a cross-tenant project.
	useEffect(() => {
		if (previousProjectParam.current !== preselectedProjectId) {
			setDraft((d) => ({
				...d,
				sampleInfo: { ...d.sampleInfo, projectId: preselectedProjectId },
			}));
			setActiveStep(0);
			submissionCheckpoint.current = {};
			setSubmissionStage("idle");
			setError(null);
		}
		previousProjectParam.current = preselectedProjectId;
	}, [preselectedProjectId]);

	useEffect(() => {
		const nextOrganizationId = activeOrganization?.id;
		if (
			previousOrganizationId.current &&
			previousOrganizationId.current !== nextOrganizationId
		) {
			setDraft((currentDraft) => ({
				...currentDraft,
				sampleInfo: { ...currentDraft.sampleInfo, projectId: null },
			}));
			setActiveStep(0);
			submissionCheckpoint.current = {};
			setSubmissionStage("idle");
			setError(null);
		}
		previousOrganizationId.current = nextOrganizationId;
	}, [activeOrganization?.id]);

	// Phase 11 — load plan entitlements so locked levels stay visible.
	useEffect(() => {
		let cancelled = false;
		void getApiClient()
			.getMyUsage()
			.then((res) => {
				if (cancelled) return;
				const e = res.usage.entitlements;
				setPlanAccess({
					allowPreliminary: e.allowPreliminary,
					allowModerate: e.allowModerate,
					allowAdvanced: e.allowAdvanced,
					tierLabel: e.grandfathered
						? `${e.tier} (demo)`
						: e.tier,
				});
				// If current draft level is locked, fall back to Preliminary.
				if (
					(draft.testLevel === SoilTestLevel.MODERATE && !e.allowModerate) ||
					(draft.testLevel === SoilTestLevel.ADVANCED && !e.allowAdvanced)
				) {
					setDraft((d) => ({ ...d, testLevel: SoilTestLevel.PRELIMINARY }));
				}
			})
			.catch(() => {
				if (!cancelled) setPlanAccess(null);
			});
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only re-fetch on org switch
	}, [activeOrganization?.id]);

	const steps = useMemo(
		() => visibleStepsForLevel(draft.testLevel),
		[draft.testLevel]
	);
	const current = steps[activeStep];
	const isLast = activeStep === steps.length - 1;
	const projectSelected = !!draft.sampleInfo.projectId;
	const canAdvance =
		current?.key === "sample-info" ? projectSelected : true;

	const next = () => setActiveStep((s) => Math.min(s + 1, steps.length - 1));
	const back = () => setActiveStep((s) => Math.max(s - 1, 0));

	useEffect(() => {
		if (previousStep.current !== activeStep) {
			stepContentRef.current?.focus();
			previousStep.current = activeStep;
		}
	}, [activeStep]);

	const handleSubmit = async () => {
		if (!draft.sampleInfo.projectId) {
			setError("Please select a project before submitting the soil test.");
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const client = getApiClient();
			let sampleId = submissionCheckpoint.current.sampleId;
			if (!sampleId) {
				const sample = await client.createSoilSample({
					...draft.sampleInfo,
					projectId: draft.sampleInfo.projectId,
				});
				sampleId = sample.sample.id;
				submissionCheckpoint.current.sampleId = sampleId;
				setSubmissionStage("sample-created");
			}

			let soilTestId = submissionCheckpoint.current.soilTestId;
			if (!soilTestId) {
				const test = await client.createSoilTest(
					toCreateSoilTestRequest(draft, sampleId)
				);
				soilTestId = test.soilTest.id;
				submissionCheckpoint.current.soilTestId = soilTestId;
				setSubmissionStage("test-created");
			}
			// Kick off calculation so the detail page has results to show.
			// Failures here surface to the user but the test itself is
			// already persisted, so the detail page will still render.
			//
			// PRELIMINARY soil tests collect pH/EC/TDS only — they do
			// not capture CEC or Ca/Mg/K/Na, so requesting the chemistry
			// engine for them is a known mismatch (it would either
			// throw "resolved CEC is zero" or be silently skipped by
			// the backend). MODERATE and ADVANCED carry the cation
			// panel needed by the chemistry engine.
			const runChemistry =
				draft.testLevel !== SoilTestLevel.PRELIMINARY;
			await client.calculateSoilTest(soilTestId, {
				runPhysics: true,
				runChemistry,
				runInterpretation: true,
				calculationMode: "LAB",
			});
			navigate(`/soil-tests/${soilTestId}`);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
			requestAnimationFrame(() => errorRef.current?.focus());
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Box
			data-testid="soil-test-wizard"
			data-layout={isCompact ? "compact" : "wide"}
			data-motion={prefersReducedMotion ? "reduced" : "full"}
			sx={{ maxWidth: 960, mx: "auto", minWidth: 0 }}
		>
			{error && (
				<Alert
					ref={errorRef}
					tabIndex={-1}
					severity="error"
					sx={{ mb: 2 }}
					onClose={() => setError(null)}
				>
					{error}. {submissionStage === "test-created"
						? "The sample and soil test already exist; retry resumes calculation without recreating them."
						: submissionStage === "sample-created"
							? "The sample already exists; retry resumes soil-test creation without recreating the sample."
							: "No sample or soil test was created."}
				</Alert>
			)}

			<SoilTestStepper steps={steps} activeStep={activeStep} />

			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ mb: 2, textAlign: "center" }}
			>
				Step {activeStep + 1} of {steps.length}
				{current?.label ? ` · ${current.label}` : ""}
			</Typography>

			<GlassSurface
				ref={stepContentRef}
				tabIndex={-1}
				component="section"
				aria-label={current?.label ?? "Wizard step"}
				aria-live="polite"
				intensity="strong"
				sx={{ p: { xs: 2, sm: 3 }, mb: 2, minWidth: 0 }}
			>
				{current?.key === "sample-info" && (
					<SampleInfoStep
						value={draft.sampleInfo}
						onChange={(sampleInfo) => setDraft({ ...draft, sampleInfo })}
					/>
				)}
				{current?.key === "test-level" && (
					<TestLevelStep
						value={draft.testLevel}
						onChange={(testLevel) => setDraft({ ...draft, testLevel })}
						planAccess={planAccess}
					/>
				)}
				{current?.key === "preliminary" && (
					<PreliminaryInputStep draft={draft} onChange={setDraft} />
				)}
				{current?.key === "moderate" && (
					<ModerateInputStep draft={draft} onChange={setDraft} />
				)}
				{current?.key === "advanced" && (
					<AdvancedInputStep draft={draft} onChange={setDraft} />
				)}
				{current?.key === "review" && <ReviewStep draft={draft} />}
			</GlassSurface>

			<Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={2} justifyContent="flex-end">
				<Button onClick={back} disabled={activeStep === 0 || submitting || submissionStage !== "idle"}>
					Back
				</Button>
				{!isLast && (
					<Button
						variant="contained"
						onClick={next}
						disabled={!canAdvance || submitting || submissionStage !== "idle"}
					>
						Next
					</Button>
				)}
				{isLast && (
					<Button
						variant="contained"
						color="primary"
						onClick={() => void handleSubmit()}
						disabled={submitting || !projectSelected}
						aria-busy={submitting}
					>
						{submitting ? (
							<><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Submitting…</>
						) : error && submissionStage !== "idle" ? "Resume submission" : "Submit soil test"}
					</Button>
				)}
			</Stack>
		</Box>
	);
}
